// /press — pressrum. Källa för journalister: presskontakt, mediakit,
// loggor, snabba fakta-pekare och pressmeddelanden. Schema: NewsMediaOrganization
// + ContactPoint för "press". Person-schema för presskontakt.

import { createFileRoute, Link } from "@tanstack/react-router";
import { Section } from "@/components/site/Section";
import { Button } from "@/components/site/Button";
import { breadcrumbs, person, speakable, aeoGeo } from "@/lib/seo/schema";
import { abs, ORG_ID , SOCIAL_IMAGE_URL} from "@/lib/seo/constants";

const PATH = "https://fenny.se/press";
const PRESS_CONTACT = {
  name: "Fenny-redaktionen",
  role: "Press & kommunikation",
  email: "press@fenny.se",
};

const QUICK_FACTS = [
  { k: "Grundat", v: "Norrhavet Group" },
  { k: "Bas", v: "Stockholm, Göteborg, Malmö" },
  { k: "Tjänster", v: "Pulse (filmabonnemang) & Prime (premium reklamfilm)" },
  { k: "Produktioner / år", v: "120+ filmleveranser 2025" },
  { k: "Kategorier", v: "B2B, SaaS, industri, e-handel, employer branding" },
];

const PRESS_RELEASES = [
  {
    date: "2026-05-19",
    title: "Fenny släpper branschrapport: Reklamfilm i Sverige 2026",
    path: "/rapport/reklamfilm-2026",
    excerpt:
      "120+ produktioner sammanställda till öppna prisspann, lead-tider och format-benchmarks för svensk reklamfilm 2026.",
  },
];

export const Route = createFileRoute("/press")({
  head: () => {
    const title = "Press och pressbilder — kontakt och material";
    const description =
      "Pressrum för Fenny: presskontakt, mediakit, snabba fakta och pressmeddelanden. För journalister, podcasts och redaktioner.";
    const ogImage = SOCIAL_IMAGE_URL;

    const pressOrgLd = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "@id": ORG_ID,
      url: abs("/"),
      contactPoint: [
        {
          "@type": "ContactPoint",
          contactType: "Press",
          email: PRESS_CONTACT.email,
          areaServed: "SE",
          availableLanguage: ["sv", "en"],
        },
      ],
    };

    return {
      meta: [
        { title: `${title} | Fenny` },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: PATH },
        { property: "og:image", content: ogImage },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: PATH }],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/press"})) },
        { type: "application/ld+json", children: JSON.stringify(pressOrgLd) },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            person({
              name: PRESS_CONTACT.name,
              jobTitle: PRESS_CONTACT.role,
              path: PATH,
            }),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            breadcrumbs([
              { name: "Hem", path: "/" },
              { name: "Press", path: PATH },
            ]),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(speakable(["[data-speakable]", "h1"])),
        },
      ],
    };
  },
  component: PressPage,
});

function PressPage() {
  return (
    <article>
      <Section className="pt-16">
        <div className="mx-auto max-w-3xl">
          <div className="mb-3 text-xs uppercase tracking-[0.2em] text-mint">
            Pressrum
          </div>
          <h1 className="text-balance text-4xl font-semibold md:text-5xl">
            Press
          </h1>
          <p
            data-speakable
            className="mt-5 text-lg text-foreground/85 md:text-xl"
          >
            Pressmaterial, snabba fakta och citerbara siffror om svensk
            film-/videoproduktion 2026. Alla siffror i våra rapporter är fria
            att citera mot källangivelse till fenny.se.
          </p>
        </div>
      </Section>

      <Section>
        <div className="mx-auto grid max-w-3xl gap-12">
          <div>
            <h2 className="text-2xl font-semibold md:text-3xl">Presskontakt</h2>
            <div className="mt-4 rounded-2xl border border-mint/20 bg-mint/[0.04] p-6">
              <div className="text-lg font-semibold">{PRESS_CONTACT.name}</div>
              <div className="text-sm text-foreground/70">
                {PRESS_CONTACT.role}
              </div>
              <a
                href={`mailto:${PRESS_CONTACT.email}`}
                className="mt-3 inline-block text-mint underline underline-offset-4"
              >
                {PRESS_CONTACT.email}
              </a>
            </div>
          </div>

          <div>
            <h2 className="text-2xl font-semibold md:text-3xl">Snabba fakta</h2>
            <dl className="mt-4 grid gap-3 sm:grid-cols-2">
              {QUICK_FACTS.map((f) => (
                <div
                  key={f.k}
                  data-speakable
                  className="rounded-2xl border border-foreground/10 bg-foreground/[0.03] p-4"
                >
                  <dt className="text-xs uppercase tracking-wider text-foreground/60">
                    {f.k}
                  </dt>
                  <dd className="mt-1 text-base">{f.v}</dd>
                </div>
              ))}
            </dl>
          </div>

          <div>
            <h2 className="text-2xl font-semibold md:text-3xl">Pressmeddelanden</h2>
            <ul className="mt-4 space-y-4">
              {PRESS_RELEASES.map((r) => (
                <li
                  key={r.path}
                  className="rounded-2xl border border-foreground/10 p-5"
                >
                  <time
                    dateTime={r.date}
                    className="text-xs uppercase tracking-wider text-mint"
                  >
                    {new Date(r.date).toLocaleDateString("sv-SE")}
                  </time>
                  <h3 className="mt-1 text-lg font-semibold">
                    <Link
                      to={r.path}
                      className="hover:underline underline-offset-4"
                    >
                      {r.title}
                    </Link>
                  </h3>
                  <p className="mt-2 text-foreground/80">{r.excerpt}</p>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-3xl border border-mint/25 bg-mint/[0.06] p-6 md:p-8">
            <h2 className="text-xl font-semibold">Mediakit & rådata</h2>
            <p className="mt-2 text-foreground/85">
              Logotyper, högupplösta porträtt och rådata-export från senaste
              branschrapporten finns att hämta på begäran. Maila{" "}
              <a
                href={`mailto:${PRESS_CONTACT.email}`}
                className="underline underline-offset-2"
              >
                {PRESS_CONTACT.email}
              </a>{" "}
              eller hämta öppen statistik direkt på{" "}
              <a
                href="/api/public/branschstats"
                className="underline underline-offset-2"
              >
                /api/public/branschstats
              </a>
              .
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <Button to="/rapport/reklamfilm-2026" variant="mint" arrow>
                Branschrapport 2026
              </Button>
              <Button to="/om-fenny" variant="outline">
                Om Fenny
              </Button>
            </div>
          </div>
        </div>
      </Section>
    </article>
  );
}
